/*
  /account — profile, password, sessions, danger zone.
  Required: signed-in user. Anonymous visitors are bounced to /login.
*/
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  User as UserIcon, Lock, AlertTriangle, Loader2, Save, Monitor,
  LogOut, Shield, X, Trash2,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { account, sessions as sessionsApi } from "../lib/accountApi";
import { toast } from "sonner";

function Section({ title, desc, icon: Icon, children, danger }) {
  return (
    <section
      data-testid={`account-section-${(title || "").toLowerCase().replace(/\s+/g, "-")}`}
      className="card-tactile rounded-2xl p-5 sm:p-6"
      style={danger ? { borderColor: "color-mix(in srgb, var(--error) 35%, var(--border-subtle))" } : {}}
    >
      <div className="flex items-start gap-3 mb-5">
        {Icon && (
          <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
               style={{ background: danger ? "color-mix(in srgb, var(--error) 12%, transparent)" : "var(--primary-soft)",
                        color: danger ? "var(--error)" : "var(--primary-ink)" }}>
            <Icon className="h-5 w-5" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h2 className="font-display tracking-tight text-xl" style={{ color: danger ? "var(--error)" : "var(--text-main)" }}>{title}</h2>
          {desc && <p className="text-sm mt-0.5" style={{ color: "var(--text-muted)" }}>{desc}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-caps block mb-1">{label}</span>
      {children}
    </label>
  );
}

function inputCls() {
  return "w-full px-3 h-10 rounded-lg border bg-transparent text-sm";
}

export default function AccountPage() {
  const { user, loading, refresh, logout } = useAuth();
  const nav = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const [delPw, setDelPw] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/login", { replace: true });
  }, [user, loading, nav]);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      loadSessions();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadSessions() {
    setLoadingSessions(true);
    try {
      const list = await sessionsApi.list();
      setSessions(list);
    } catch { /* noop */ }
    finally { setLoadingSessions(false); }
  }

  async function saveProfile(e) {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const patch = {};
      if (name.trim() !== (user.name || "")) patch.name = name.trim();
      if (email.trim() !== user.email) patch.email = email.trim();
      if (Object.keys(patch).length === 0) {
        toast.info("Nothing to save");
        return;
      }
      await account.updateProfile(patch);
      await refresh();
      toast.success("Profile updated");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't update profile");
    } finally { setSavingProfile(false); }
  }

  async function savePw(e) {
    e.preventDefault();
    setSavingPw(true);
    try {
      await account.changePassword(curPw, newPw);
      setCurPw(""); setNewPw("");
      toast.success("Password updated");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't change password");
    } finally { setSavingPw(false); }
  }

  async function revokeOne(id) {
    if (!window.confirm("Sign out this device?")) return;
    try {
      await sessionsApi.revoke(id);
      toast.success("Session revoked");
      const cur = sessions.find(s => s.id === id);
      if (cur?.is_current) {
        await logout();
        nav("/login");
      } else loadSessions();
    } catch { toast.error("Couldn't revoke"); }
  }

  async function revokeAll() {
    if (!window.confirm("Sign out from every other device?")) return;
    try {
      const r = await sessionsApi.revokeAll(true);
      toast.success(`Signed out ${r.revoked} other session(s)`);
      loadSessions();
    } catch { toast.error("Couldn't revoke sessions"); }
  }

  async function doDelete(e) {
    e.preventDefault();
    if (delConfirm !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    setDeleting(true);
    try {
      await account.deleteAccount(delPw);
      toast.success("Account deleted. Goodbye 👋");
      await logout();
      nav("/", { replace: true });
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't delete");
    } finally { setDeleting(false); }
  }

  if (loading || !user) {
    return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto w-full space-y-6 py-2" data-testid="account-page">
      <div>
        <h1 className="font-display tracking-tighter text-3xl sm:text-4xl mb-1" style={{ color: "var(--primary-ink)" }}>
          Your account
        </h1>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Signed in as <strong>{user.email}</strong>. <Link to="/" className="hover:underline" style={{ color: "var(--primary-ink)" }}>Back to Hub</Link>
        </p>
      </div>

      <Section title="Profile" desc="Update your display name and email." icon={UserIcon}>
        <form onSubmit={saveProfile} className="space-y-3">
          <Field label="Name">
            <input
              data-testid="account-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls()}
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              data-testid="account-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls()}
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
            />
          </Field>
          <button
            type="submit"
            disabled={savingProfile}
            data-testid="account-profile-save"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: "var(--primary)" }}
          >
            {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save changes
          </button>
        </form>
      </Section>

      <Section title="Password" desc="Change the password used to sign in." icon={Lock}>
        <form onSubmit={savePw} className="space-y-3">
          <Field label="Current password">
            <input
              type="password"
              data-testid="account-current-pw"
              required
              value={curPw}
              onChange={(e) => setCurPw(e.target.value)}
              className={inputCls()}
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
              autoComplete="current-password"
            />
          </Field>
          <Field label="New password (min 6 chars)">
            <input
              type="password"
              data-testid="account-new-pw"
              required
              minLength={6}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className={inputCls()}
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
              autoComplete="new-password"
            />
          </Field>
          <button
            type="submit"
            disabled={savingPw}
            data-testid="account-password-save"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: "var(--primary)" }}
          >
            {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Update password
          </button>
        </form>
      </Section>

      <Section title="Active sessions" desc="Devices signed into this account. Revoke any you don't recognize." icon={Shield}>
        <div className="space-y-2">
          {loadingSessions && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>}
          {!loadingSessions && sessions.map(s => (
            <div key={s.id}
                 data-testid={`session-row-${s.id}`}
                 className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
                 style={{ borderColor: "var(--border-subtle)" }}>
              <Monitor className="h-5 w-5 shrink-0" style={{ color: "var(--text-muted)" }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  {s.label}
                  {s.is_current && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                          style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}>
                      This device
                    </span>
                  )}
                </div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                  Last active {new Date(s.last_used_at).toLocaleString()} · IP {s.ip || "—"}
                </div>
              </div>
              <button
                onClick={() => revokeOne(s.id)}
                data-testid={`session-revoke-${s.id}`}
                title="Sign out this device"
                className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[color:var(--surface-2)]"
                style={{ color: "var(--text-muted)" }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          {!loadingSessions && sessions.length > 1 && (
            <button
              onClick={revokeAll}
              data-testid="sessions-revoke-all"
              className="mt-2 inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold border"
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
            >
              <LogOut className="h-4 w-4" /> Sign out other devices
            </button>
          )}
        </div>
      </Section>

      <Section title="Danger zone" desc="This permanently deletes your account, all data, and any family you own. Cannot be undone." icon={AlertTriangle} danger>
        <form onSubmit={doDelete} className="space-y-3">
          <Field label="Confirm with your password">
            <input
              type="password"
              data-testid="account-delete-pw"
              required
              value={delPw}
              onChange={(e) => setDelPw(e.target.value)}
              className={inputCls()}
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
              autoComplete="current-password"
            />
          </Field>
          <Field label="Type DELETE to proceed">
            <input
              data-testid="account-delete-confirm"
              required
              value={delConfirm}
              onChange={(e) => setDelConfirm(e.target.value)}
              placeholder="DELETE"
              className={inputCls()}
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
            />
          </Field>
          <button
            type="submit"
            disabled={deleting || delConfirm !== "DELETE"}
            data-testid="account-delete-submit"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: "var(--error)" }}
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete my account permanently
          </button>
        </form>
      </Section>
    </div>
  );
}
